import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Shield, Globe, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm} ${hh}:${mi}`;
  } catch { return iso; }
};

const SecurityLogCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/api/admin/security-log?hours=24&limit=5`,
        { headers: authHeaders() }
      );
      setData(r.data);
    } catch { /* silent for non-admin */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return null;
  if (!data || data.total === 0) return null;

  const topRows = expanded ? data.top : data.top.slice(0, 2);

  return (
    <Card className="border-slate-200 bg-slate-50/40" data-testid="security-log-card">
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <Shield className="h-4 w-4 text-slate-500 flex-shrink-0" />
            <p className="text-xs font-semibold text-slate-700 truncate">
              {data.total} failed login{data.total > 1 ? 's' : ''} in last 24h
              <span className="ml-1.5 text-slate-400 font-normal">
                across {data.top.length} IP{data.top.length > 1 ? 's' : ''}
              </span>
            </p>
          </div>
          {data.top.length > 2 && (
            <button
              onClick={() => setExpanded(v => !v)}
              className="text-[10px] text-slate-500 hover:text-slate-800 flex items-center gap-0.5"
              data-testid="security-log-toggle"
            >
              {expanded ? 'less' : 'more'} <ChevronRight className={`h-3 w-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </button>
          )}
        </div>

        <div className="space-y-1">
          {topRows.map(r => (
            <div
              key={r.ip}
              className="flex items-center gap-2 text-[11px] bg-white border border-slate-200 rounded-md px-2 py-1"
              data-testid={`security-row-${r.ip.replace(/\./g, '-')}`}
            >
              <Globe className="h-3 w-3 text-slate-400 flex-shrink-0" />
              <span className="font-mono text-slate-700 flex-shrink-0">{r.ip}</span>
              <Badge variant="secondary" className="h-4 px-1.5 text-[9px] font-semibold flex-shrink-0">
                {r.count}
              </Badge>
              <div className="flex gap-0.5 flex-shrink-0">
                {r.sources.map(s => (
                  <Badge
                    key={s}
                    variant="outline"
                    className={`h-4 px-1 text-[9px] py-0 ${s === 'admin'
                      ? 'border-rose-200 text-rose-600 bg-rose-50'
                      : 'border-sky-200 text-sky-600 bg-sky-50'}`}
                  >
                    {s}
                  </Badge>
                ))}
              </div>
              <span className="text-slate-400 truncate flex-1 min-w-0">
                {r.principals.join(', ')}
              </span>
              <span className="text-slate-400 text-[10px] flex-shrink-0">{fmtTime(r.last_at)}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default SecurityLogCard;
